import { secureStorage } from '../secureStorage';
import { patientStorage } from '../patientStorage';

/**
 * The single place a login response is turned into a stored session token.
 *
 * WHY THIS EXISTS. The app has two sources of truth for "am I logged in": the server trusts its
 * httpOnly `auth_token` cookie, and the client gates on a token in storage
 * (`AuthContext.initializeAuth` → `secureStorage.getAuthToken()`). Both must be written, and only
 * the *client* half is easy to forget — because forgetting it breaks nothing visible. Every API
 * call still succeeds on the cookie, so the app looks completely healthy right up until the user
 * reloads the page, at which point the client sees no token and logs them out.
 *
 * That is exactly what happened to `/auth/mfa/verify`: it was written as a plain `fetchApi`
 * passthrough while `login()` and `refresh()` each stored the token by hand. The bug was invisible
 * in normal use and only affected MFA-enabled accounts.
 *
 * The fix is structural rather than another hand-rolled copy: capturing is now one function, so a
 * newly added session-establishing endpoint either calls it or has no session at all — a failure
 * that shows up immediately instead of only after a refresh.
 *
 * If you are adding an endpoint that establishes or renews a session, call the matching capture
 * function here. Do not call `setAuthToken` directly.
 */

/**
 * Pull the access token out of a login-shaped response.
 *
 * Handles both envelope depths because `fetchApi` returns `{ data: … }` while the backend itself
 * wraps payloads in `ApiResponse` — so a token can arrive as `data.token` or `data.data.token`
 * depending on the endpoint. Getting this nesting wrong is silent (you get `undefined`, not an
 * error), which is precisely why it belongs in one place.
 */
export function extractSessionToken(response: unknown): string | null {
  if (!response || typeof response !== 'object') return null;
  const outer = (response as { data?: unknown }).data;
  if (!outer || typeof outer !== 'object') return null;

  const inner = (outer as { data?: unknown }).data;
  const candidate =
    (inner && typeof inner === 'object' ? (inner as { token?: unknown }).token : undefined) ??
    (outer as { token?: unknown }).token;

  return typeof candidate === 'string' && candidate.length > 0 ? candidate : null;
}

/**
 * Capture a staff/doctor/admin session token. Call from any endpoint that establishes or renews a
 * staff session (login, MFA verify, refresh).
 *
 * @returns the token when one was found and stored, otherwise null. A null return is normal for
 *          responses that legitimately carry no session yet — notably an `mfaRequired` login, where
 *          the password was correct but the second factor has not been provided.
 */
export function captureStaffSession(response: unknown): string | null {
  const token = extractSessionToken(response);
  if (token) secureStorage.setAuthToken(token);
  return token;
}

/** Patient-portal equivalent of {@link captureStaffSession}. Writes to patientStorage. */
export function capturePatientSession(response: unknown): string | null {
  const token = extractSessionToken(response);
  if (token) patientStorage.setAuthToken(token);
  return token;
}
